// ============================================================
//  Editierbarer Cutover-Plan — eigener Store (Muster: store/plan.ts), bleibt über Tab-Wechsel
//  und Reload erhalten (localStorage). src/data/landscapes.ts bleibt der RFP-Plan; hier liegen
//  nur die Überschreibungen je Landschaft: { L6: "2027-09-30" }.
//
//  Ein verschobener Cutover ändert nur, WANN der Bedarf eines Systems zählt — nie WIE VIEL.
//  Der Anker einer Person (frühester Cutover ihrer FUE-Landschaften) wandert mit der Reihenfolge.
// ============================================================
import { useSyncExternalStore } from "react";
import type { Landscape } from "../domain/types";
import { landscapes as rfpLandscapes } from "../data/landscapes";
import { gateStatusStore } from "./gateStatus";

export type CutoverOverrides = Record<string, string>;

export const CUTOVER_STORE_KEY = "rise-cockpit.cutover.v1";

const ISO = /^\d{4}-\d{2}-\d{2}$/;
const knownIds = new Set(rfpLandscapes.map((l) => l.id));

function load(): CutoverOverrides {
  try {
    const raw = localStorage.getItem(CUTOVER_STORE_KEY);
    if (!raw) return {};
    const j = JSON.parse(raw);
    if (!j || typeof j !== "object" || Array.isArray(j)) return {};
    const out: CutoverOverrides = {};
    for (const [id, v] of Object.entries(j)) if (knownIds.has(id) && typeof v === "string" && ISO.test(v)) out[id] = v;
    return out;
  } catch { return {}; }
}
function persist() {
  try {
    if (Object.keys(overrides).length === 0) localStorage.removeItem(CUTOVER_STORE_KEY);
    else localStorage.setItem(CUTOVER_STORE_KEY, JSON.stringify(overrides));
  } catch { /* privat/blockiert: nur im Speicher */ }
}

let overrides: CutoverOverrides = load();
let effective: Landscape[] = apply(overrides);
const listeners = new Set<() => void>();

/** Landschaften in Dateireihenfolge, Cutover-Datum überschrieben, wo eine Überschreibung liegt. */
function apply(o: CutoverOverrides): Landscape[] {
  return rfpLandscapes.map((l) => (o[l.id] && o[l.id] !== l.cutover ? { ...l, cutover: o[l.id] } : l));
}

function commit(next: CutoverOverrides) {
  overrides = next;
  effective = apply(next);
  persist();
  for (const l of listeners) l();
}
function subscribe(l: () => void) { listeners.add(l); return () => { listeners.delete(l); }; }
const getSnapshot = () => effective;

export const cutoverStore = {
  /** Aktuelle Überschreibungen { landscapeId: ISO-Datum } — Kopie, nicht die Referenz. */
  overrides: (): CutoverOverrides => ({ ...overrides }),
  /** Ist das Datum dieser Landschaft gegenüber dem RFP-Plan geändert? */
  isOverridden: (id: string): boolean => id in overrides,
  /** RFP-Datum dieser Landschaft (aus src/data/landscapes.ts). */
  rfpCutover: (id: string): string | undefined => rfpLandscapes.find((l) => l.id === id)?.cutover,
  /**
   * Setzt das Cutover-Datum einer Landschaft. Ungültige Werte werden ignoriert;
   * das RFP-Datum selbst hebt die Überschreibung auf.
   */
  set(id: string, isoDate: string) {
    if (!knownIds.has(id) || !ISO.test(isoDate)) return;
    const rfp = cutoverStore.rfpCutover(id);
    const next = { ...overrides };
    if (isoDate === rfp) delete next[id]; else next[id] = isoDate;
    commit(next);
  },
  /** Hebt die Überschreibung einer Landschaft auf. */
  clear(id: string) {
    if (!(id in overrides)) return;
    const next = { ...overrides }; delete next[id]; commit(next);
  },
  /** Zurück auf den RFP-Plan — alle Überschreibungen weg, gesetzte Gate-Status ebenfalls. */
  reset() { if (Object.keys(overrides).length) commit({}); gateStatusStore.reset(); },
};

/** Nicht-reaktiv, für Domain-Code: alle Landschaften mit wirksamen Cutover-Daten (Dateireihenfolge). */
export function effectiveLandscapes(): Landscape[] { return effective; }

/** Nicht-reaktiv: zugeordnete Landschaften (ohne „Zuordnung offen") mit wirksamen Cutover-Daten. */
export function effectiveAssignedLandscapes(): Landscape[] { return effective.filter((l) => !l.open); }

/** Reaktiv: alle Landschaften mit wirksamen Cutover-Daten, gleiche Reihenfolge wie src/data/landscapes.ts. */
export function useLandscapes(): Landscape[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Reaktiv: zugeordnete Landschaften (ohne „Zuordnung offen") mit wirksamen Cutover-Daten. */
export function useAssignedLandscapes(): Landscape[] {
  const all = useLandscapes();
  return all.filter((l) => !l.open);
}

/** Reaktiv: die Überschreibungen selbst — für Markierungen „geändert, RFP-Plan: …". */
export function useCutoverOverrides(): CutoverOverrides {
  useLandscapes(); // an den Store binden; Snapshot-Referenz wechselt bei jeder Änderung
  return overrides;
}
